import {
  calculateBuyout,
  DEFAULT_TERMINATION_RULES,
  type BuyoutBreakdown,
  type BuyoutScheduleEntry,
  type TerminationRules,
  type TerminationType,
} from "@workspace/buyout";

export const TERMINATION_OFFBOARDING_STEPS = [
  { id: 1, label: "Reason" },
  { id: 2, label: "Buyout" },
  { id: 3, label: "Access" },
  { id: 4, label: "Confirm" },
] as const;

export type TerminationOffboardingStep = (typeof TERMINATION_OFFBOARDING_STEPS)[number]["id"];

export const TERMINATION_TYPE_LABELS: Record<TerminationType, string> = {
  without_cause_university: "Without cause (university)",
  with_cause: "With cause",
  coach_initiated: "Coach-initiated departure",
};

export interface TerminationOffboardingState {
  terminationDate: string;
  terminationType: TerminationType;
  reason: string;
  buyoutReviewed: boolean;
  buyoutObligationApplies: boolean | null;
  mitigationEnabled: boolean;
  mitigationAmount: number;
  revokePortalAccess: boolean;
  revokeGuestAccess: boolean;
  checklistReturnEquipment: boolean;
  checklistHrPayroll: boolean;
  checklistBenefits: boolean;
  confirmTyped: string;
}

export function defaultTerminationOffboardingState(
  overrides?: Partial<TerminationOffboardingState>,
): TerminationOffboardingState {
  return {
    terminationDate: new Date().toISOString().split("T")[0],
    terminationType: "without_cause_university",
    reason: "",
    buyoutReviewed: false,
    buyoutObligationApplies: null,
    mitigationEnabled: false,
    mitigationAmount: 0,
    revokePortalAccess: true,
    revokeGuestAccess: true,
    checklistReturnEquipment: false,
    checklistHrPayroll: false,
    checklistBenefits: false,
    confirmTyped: "",
    ...overrides,
  };
}

export function canAdvanceTerminationStep(
  step: TerminationOffboardingStep,
  state: TerminationOffboardingState,
): boolean {
  if (step === 1) {
    return Boolean(state.terminationDate) && Boolean(state.terminationType);
  }
  if (step === 2) {
    return state.buyoutReviewed && state.buyoutObligationApplies !== null;
  }
  if (step === 3) {
    return true;
  }
  return state.confirmTyped.trim().toUpperCase() === "TERMINATE";
}

export interface BuyoutEstimateInput {
  startDate?: string | null;
  endDate?: string | null;
  annualSalary?: number | string | null;
  buyoutClause?: number | string | null;
  buyoutSchedule?: BuyoutScheduleEntry[] | null;
  terminationRules?: TerminationRules | null;
  terminationDate: string;
  terminationType: TerminationType;
  mitigationEnabled: boolean;
  mitigationAmount: number;
}

export function estimateTerminationBuyout(
  input: BuyoutEstimateInput,
): BuyoutBreakdown | null {
  if (!input.startDate || !input.endDate || input.annualSalary == null || input.annualSalary === "") {
    return null;
  }
  const annualSalary =
    typeof input.annualSalary === "string"
      ? parseFloat(input.annualSalary)
      : Number(input.annualSalary);
  if (!Number.isFinite(annualSalary)) return null;

  const buyoutClauseRaw =
    input.buyoutClause == null || input.buyoutClause === ""
      ? null
      : typeof input.buyoutClause === "string"
        ? parseFloat(input.buyoutClause)
        : Number(input.buyoutClause);

  try {
    return calculateBuyout({
      startDate: input.startDate,
      endDate: input.endDate,
      annualSalary,
      buyoutClause: buyoutClauseRaw != null && Number.isFinite(buyoutClauseRaw) ? buyoutClauseRaw : null,
      buyoutSchedule: (input.buyoutSchedule ?? []) as BuyoutScheduleEntry[],
      terminationRules: (input.terminationRules ?? DEFAULT_TERMINATION_RULES) as TerminationRules,
      terminationDate: input.terminationDate,
      terminationType: input.terminationType,
      mitigationEnabled: input.mitigationEnabled,
      mitigationAmount: Number(input.mitigationAmount) || 0,
    });
  } catch {
    return null;
  }
}

export function buildTerminationOffboardingNotes(args: {
  state: TerminationOffboardingState;
  estimatedBuyout: number | null;
  priorNotes?: string | null;
}): string {
  const { state, estimatedBuyout, priorNotes } = args;
  const lines = [
    "— Termination offboarding —",
    `Effective date: ${state.terminationDate}`,
    `Type: ${TERMINATION_TYPE_LABELS[state.terminationType]}`,
    state.reason.trim() ? `Reason: ${state.reason.trim()}` : null,
    `Buyout reviewed: yes`,
    `Buyout obligation applies: ${state.buyoutObligationApplies ? "yes" : "no"}`,
    estimatedBuyout != null
      ? `Estimated buyout: $${Math.round(estimatedBuyout).toLocaleString()}`
      : "Estimated buyout: unavailable",
    state.mitigationEnabled
      ? `Mitigation assumed: $${Math.round(state.mitigationAmount || 0).toLocaleString()}`
      : null,
    `Coach portal access revoked: ${state.revokePortalAccess ? "yes" : "no"}`,
    `Contract guest access revoked: ${state.revokeGuestAccess ? "yes" : "no"}`,
    state.checklistReturnEquipment ? "Checklist: equipment / property return noted" : null,
    state.checklistHrPayroll ? "Checklist: HR / payroll notified" : null,
    state.checklistBenefits ? "Checklist: benefits / insurance follow-up noted" : null,
  ].filter(Boolean) as string[];

  const block = lines.join("\n");
  const prior = (priorNotes ?? "").trim();
  if (!prior) return block;
  return `${prior}\n\n${block}`;
}
